import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  Observable,
  throwError,
  BehaviorSubject,
  filter,
  take,
  switchMap,
  catchError,
} from 'rxjs';
import { StorageService } from '@victory-lib-ui/shared/utils';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '../config/environment.config';

// Track if we're currently refreshing the token
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

type RefreshTokenResponse = {
  success: boolean;
  data?: {
    access_token?: string;
    refresh_token?: string;
  };
};

/**
 * Auth Interceptor
 * Adds authentication token to outgoing HTTP requests and handles token refresh
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const storageService = inject(StorageService);
  const http = inject(HttpClient);
  const router = inject(Router);
  const environment = inject(ENVIRONMENT_CONFIG);

  // Get the auth token from storage
  const token =
    storageService.getItem<string>('auth_token', 'local') ||
    storageService.getItem<string>('auth_token', 'session');

  // Clone the request and add the authorization header if token exists
  let authReq = req;
  if (
    token &&
    !req.url.includes('/auth/login') &&
    !req.url.includes('/auth/register')
  ) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Handle the request and catch 401 errors for token refresh
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get a 401 error and it's not an auth endpoint
      if (
        error.status === HttpStatusCode.Unauthorized &&
        !req.url.includes('/auth/login') &&
        !req.url.includes('/auth/register') &&
        !req.url.includes('/auth/refresh')
      ) {
        // Check if error message indicates expired token (can be refreshed)
        const errorMessage = error.error?.message || '';
        const isExpiredToken =
          errorMessage.includes('expired') ||
          errorMessage.includes('Token has expired');

        if (isExpiredToken) {
          // Try to refresh token
          return handle401Error(
            authReq,
            next,
            storageService,
            http,
            router,
            environment
          );
        } else {
          // Invalid token or other auth issue - logout immediately
          logout(storageService, router);
          return throwError(() => error);
        }
      }

      return throwError(() => error);
    })
  );
};

/**
 * Handle 401 error by attempting to refresh the token
 * Returns the retried request or throws error - DOES NOT redirect on success
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  storageService: StorageService,
  http: HttpClient,
  router: Router,
  environment: EnvironmentConfig
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = storageService.getItem<string>(
      'refresh_token',
      'local'
    );

    if (!refreshToken) {
      // No refresh token available, logout user
      isRefreshing = false;
      logout(storageService, router);
      return throwError(() => new Error('No refresh token available'));
    }

    // Attempt to refresh the token
    return http
      .post<RefreshTokenResponse>(`${environment.apiUrl}/auth/refresh`, {
        refresh_token: refreshToken,
      })
      .pipe(
        switchMap((response) => {
          isRefreshing = false;

          if (response.success && response.data?.access_token) {
            // Store new token
            const rememberMe = !!storageService.getItem<string>(
              'auth_token',
              'local'
            );
            storageService.setItem(
              'auth_token',
              response.data.access_token,
              rememberMe ? 'local' : 'session'
            );

            // Store new refresh token if provided
            if (response.data.refresh_token) {
              storageService.setItem(
                'refresh_token',
                response.data.refresh_token,
                'local'
              );
            }

            refreshTokenSubject.next(response.data.access_token);

            // Retry the original request with new token - DO NOT REDIRECT
            // The user stays on their current page
            return next(addToken(request, response.data.access_token));
          }

          // Refresh failed, logout user
          logout(storageService, router);
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((err) => {
          isRefreshing = false;
          // Only logout on refresh failure, not on success
          logout(storageService, router);
          return throwError(() => err);
        })
      );
  } else {
    // Wait for the token refresh to complete, then retry the request
    // DO NOT REDIRECT - just retry the request with new token
    return refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) => next(addToken(request, token)))
    );
  }
}

/**
 * Add token to request headers
 */
function addToken(
  request: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Logout user and clear storage
 */
function logout(storageService: StorageService, router: Router): void {
  storageService.removeItem('user', 'local');
  storageService.removeItem('auth_token', 'local');
  storageService.removeItem('auth_token', 'session');
  storageService.removeItem('refresh_token', 'local');
  router.navigate(['/auth/login']);
}
